import { Fragment, useState, type MouseEvent } from 'react';
import { useGetOne, useNotify, useRefresh, type RaRecord } from 'react-admin';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFeature } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { FEATURE_BANK_ROW_ASSIGN, rowAssignBlockedReason, showsRowAssign } from './bankFtl';
import type { LooseRecord } from './cells';
import { CandidateCalcCells, CandidateCalcHeaders, CALC_COLUMNS } from './candidateCells';
import {
  hasCandidateDetails,
  originText,
  rankFor,
  rankText,
  useCandidateRanks,
} from './candidateRanks';
import { assignFromSuggestion, toErrorMessage } from './orderActionsApi';
import { useAssignOverride } from './AssignOverride';
import { BasisList, BasisToggle } from './SuggestionBasis';
import { assignmentNotice } from './VehicleSuggestions';
import { TeamLeaderAssignsButton, usePlannerSeesDisabledAssign } from './AssignByTeamLeader';
import { lateAssignmentWarningOf } from './variantWindow';
import { LateAssignmentWarning } from './LateAssignmentWarning';

/**
 * „Obsadź…” w wierszu banku (makieta FTL p. Mariusza, pkt 9.3: „Master ma
 * w wierszu akcję «Obsadź» rozwijającą listę wariantów z uzasadnieniem obok
 * siebie”; Etap 1, decyzja Bartka 15.10.2026; przełącznik `bankRowAssign`).
 *
 * - **Kto:** tylko uprawnienie „Obsadzanie pojazdem” (`FTL_ASSIGN` —
 *   planista główny, administracja, użytkownik; role 01.10.2026). Bez niego
 *   przycisku nie ma (ukrywamy, nie wyszarzamy — decyzja 01.10.2026).
 * - **Co:** ta sama operacja co „Obsadź” w rekomendacjach na karcie
 *   (`POST /user-offers/:id/assign-from-suggestion`, wspólny rdzeń
 *   `assignVehicle`): jedno kliknięcie, warianty zwalniane jak tam.
 * - **Uzasadnienie:** wiersz kandydata z tego samego przeliczenia co karta
 *   (`GET /user-offers/:id/candidate-ranks`) — dolot, koszt, marża, wolny
 *   od, zapas, rozładunek, uwagi i „Na jakiej podstawie”. Liczone dopiero po
 *   otwarciu okna (1–2 s), nigdy dla całej listy.
 * - **Okno, nie rozwijany wiersz:** rozwinięcie dokłada tabeli kolumnę
 *   z przyciskiem, a lista ma się mieścić przy 1366 px (decyzja 08.10.2026);
 *   okno pokazuje warianty obok siebie bez ruszania szerokości listy.
 * - **Pewny brak czasu** (22.10.2026, decyzja użytkownika pkt 3): wariant,
 *   którego realizacja pewnie nie mieści się w oknach, dostaje ostrzeżenie
 *   z obiema liczbami spóźnienia — „Obsadź tym pojazdem” zostaje aktywne
 *   (furtka Team Leadera, bez powodu), a historia zlecenia zapisze
 *   „obsadzono mimo braku czasu”.
 */

const CELL_SX = { verticalAlign: 'top' } as const;
const ACTIVE = 'active';

/** Kliknięcia w przycisku i w oknie nie mogą otwierać karty z wiersza listy. */
const stop = (e: MouseEvent) => e.stopPropagation();

export const RowAssignButton = ({ record }: { record: LooseRecord }) => {
  const on = useFeature(FEATURE_BANK_ROW_ASSIGN);
  const canAssign = useHasPermission('FTL_ASSIGN');
  // Planista widzi „Obsadź…” nieaktywny z podpowiedzią (20.10.2026,
  // `assignButtonForPlanner`); bez przełącznika — przycisku nie ma.
  const plannerSeesDisabled = usePlannerSeesDisabledAssign();
  const [open, setOpen] = useState(false);
  if (!on || !showsRowAssign(record)) return null;
  if (!canAssign) {
    return plannerSeesDisabled ? (
      <TeamLeaderAssignsButton label="Obsadź…" variant="outlined" />
    ) : null;
  }
  // Zlecenie z prezlecenia przed „Jest w TMS” (16.10.2026, pyt. 5) —
  // wyszarzone z powodem w dymku; warianty dodaje się dalej na karcie.
  const blocked = rowAssignBlockedReason(record);
  if (blocked) {
    return (
      <Tooltip title={blocked}>
        <span onClick={stop} data-testid="row-assign-blocked">
          <Button size="small" variant="outlined" sx={{ py: 0, minWidth: 0 }} disabled>
            Obsadź…
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        sx={{ py: 0, minWidth: 0 }}
        onClick={(e) => {
          stop(e);
          setOpen(true);
        }}
      >
        Obsadź…
      </Button>
      {open ? <RowAssignDialog record={record} onClose={() => setOpen(false)} /> : null}
    </>
  );
};

export const RowAssignDialog = ({
  record,
  onClose,
}: {
  record: LooseRecord;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const id = record.id as number;
  // Warianty z autorem i źródłem — ze szczegółów zlecenia (lista niesie
  // najwyżej trzy); wyliczenia — z tego samego przeliczenia co karta.
  const { data: detail, isPending } = useGetOne<RaRecord>('user-offers', { id });
  const ranks = useCandidateRanks(id, 'row-assign');
  const details = hasCandidateDetails(ranks);
  const [basisFor, setBasisFor] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  // „Obsadź mimo blokady” — Team Leader (16.10.2026, pyt. 54e).
  const override = useAssignOverride();

  const candidacies = ((detail?.candidates as RaRecord[] | undefined) ?? []).filter(
    (k) => k.status === ACTIVE,
  );
  const number = (record.externalId as string | undefined) ?? `#${id}`;

  const assign = async (carId: number, plate: string) => {
    setBusy(carId);
    const done = (result: Awaited<ReturnType<typeof assignFromSuggestion>>) => {
      const notice = assignmentNotice(result, plate);
      notify(notice.message, { type: notice.type });
      refresh();
      onClose();
    };
    try {
      done(await assignFromSuggestion(id, carId));
    } catch (err) {
      const offered = override.offer(err, async (reason) =>
        done(await assignFromSuggestion(id, carId, { reason, overrideBlocking: true })),
      );
      if (!offered) notify(toErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth onClick={stop}>
      <DialogTitle>{`Obsadź zlecenie ${number} — wybierz wariant`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Warianty obok siebie, z wyliczeniami jak w rekomendacjach. „Obsadź tym pojazdem” to ta
          sama operacja co „Obsadź” na karcie zlecenia — pozostałe warianty zostaną zwolnione.
        </Typography>
        {ranks?.refused ? (
          <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
            {`Wyliczeń dla tego zlecenia nie ma: ${ranks.refused}`}
          </Typography>
        ) : null}
        {isPending ? (
          <Typography variant="body2">Wczytywanie wariantów…</Typography>
        ) : candidacies.length === 0 ? (
          <Typography variant="body2">
            Zlecenie nie ma już aktywnych wariantów — odśwież listę.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Wariant</TableCell>
                  {details ? <CandidateCalcHeaders /> : null}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {candidacies.map((k) => {
                  const carId = Number(k.carId);
                  const car = k.car as RaRecord | undefined;
                  const plate = (car?.registrationNumber as string | undefined) ?? `#${carId}`;
                  const rank = rankFor(ranks, carId);
                  const basis = rank?.candidate?.basis ?? [];
                  const late = lateAssignmentWarningOf(rank?.candidate ?? null);
                  const addedBy = (k.addedBy as RaRecord | undefined)?.username as string | undefined;
                  return (
                    <Fragment key={String(k.id)}>
                      <TableRow data-testid={`row-assign-variant-${carId}`}>
                        <TableCell sx={CELL_SX}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {plate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {`dodał: ${addedBy ?? 'brak zapisu'}`}
                            {originText(k.origin as string | null) ? ` · ${originText(k.origin as string | null)}` : ''}
                          </Typography>
                          {car?.ftlCaretaker ? (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {`opiekun: ${String(car.ftlCaretaker)}`}
                            </Typography>
                          ) : null}
                          {rankText(rank, ranks?.total ?? null) ? (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {rankText(rank, ranks?.total ?? null)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        {details ? (
                          rank?.candidate ? (
                            <CandidateCalcCells candidate={rank.candidate} cellSx={CELL_SX} />
                          ) : (
                            <TableCell colSpan={CALC_COLUMNS} sx={{ ...CELL_SX, color: 'text.secondary' }}>
                              Brak wyliczeń dla tego pojazdu.
                            </TableCell>
                          )
                        ) : null}
                        <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }} align="right">
                          {basis.length > 0 ? (
                            <BasisToggle
                              open={basisFor === carId}
                              onToggle={() => setBasisFor((p) => (p === carId ? null : carId))}
                            />
                          ) : null}
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busy !== null}
                            onClick={() => void assign(carId, plate)}
                          >
                            Obsadź tym pojazdem
                          </Button>
                        </TableCell>
                      </TableRow>
                      {late ? (
                        <TableRow>
                          <TableCell colSpan={details ? CALC_COLUMNS + 2 : 2} sx={{ pt: 0 }}>
                            <LateAssignmentWarning warning={late} testId={`row-assign-late-${carId}`} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {basisFor === carId && basis.length > 0 ? (
                        <TableRow>
                          <TableCell colSpan={details ? CALC_COLUMNS + 2 : 2} sx={{ bgcolor: 'action.hover' }}>
                            <BasisList title={`Na jakiej podstawie liczymy ${plate}`} basis={basis} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {!details && ranks === null && !isPending && candidacies.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            Wyliczenia przy wariantach liczą się po otwarciu okna (1–2 s). Obsadzić można i bez nich.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>
      {override.dialog}
    </Dialog>
  );
};
